import React, { useState, useEffect } from 'react';
import Icon from '../components/AppIcon';
import { inAppNotificationService, subscribeToNotifications, cleanupNotifications } from '../services/inAppNotificationService';

const useEmailNotifications = () => {
  const [toastNotification, setToastNotification] = useState(null);
  const [preferences, setPreferences] = useState(null);

  const handleNewNotification = (newNotification) => {
    // Check if toast notifications are enabled in metadata
    const enableToast = newNotification?.metadata?.enable_toast;
    
    if (enableToast) {
      const toastType = getToastType(newNotification?.notification_type);
      setToastNotification({
        id: newNotification?.id,
        message: newNotification?.message,
        type: toastType,
        timestamp: new Date()
      });

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setToastNotification(null);
      }, 5000);
    }
  };

  const getToastType = (notificationType) => {
    switch (notificationType) {
      case 'email_sent':
        return 'info';
      case 'email_delivered':
        return 'success';
      case 'email_bounced': case'email_failed':
        return 'error';
      case 'email_opened': case'email_clicked':
        return 'success';
      default:
        return 'info';
    }
  };

  useEffect(() => {
    loadPreferences();

    // Subscribe to real-time notifications using new broadcast version
    subscribeToNotifications(
      handleNewNotification,
      null
    );

    return () => cleanupNotifications();
  }, []);

  const loadPreferences = async () => {
    try {
      const prefs = await inAppNotificationService?.getUserPreferences();
      setPreferences(prefs);
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const dismissToast = () => {
    setToastNotification(null);
  };

  return {
    toastNotification,
    dismissToast,
    preferences
  };
};

const EmailNotificationToast = ({ notification, onClose }) => {
  if (!notification) return null;

  const getTypeStyles = () => {
    switch (notification?.type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getIcon = () => {
    switch (notification?.type) {
      case 'success':
        return 'CheckCircle';
      case 'error':
        return 'XCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'info':
      default:
        return 'Mail';
    }
  };

  return (
    <div
      className={`fixed top-24 right-4 z-50 max-w-md w-full shadow-lg rounded-lg border p-4 flex items-start gap-3 animate-slide-in-right ${getTypeStyles()}`}
      role="alert"
    >
      <Icon name={getIcon()} size={20} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium">{notification?.message}</p>
      </div>
      <button
        onClick={onClose}
        className="flex-shrink-0 hover:opacity-70 transition-opacity"
        aria-label="Close notification"
      >
        <Icon name="X" size={16} />
      </button>
    </div>
  );
};

export { useEmailNotifications, EmailNotificationToast };